/**
 * Environment and domain helpers
 */
export const isLocalEnvironment = (hostname) => {
    if (!hostname) return false;
    return hostname === 'localhost' ||
        hostname.startsWith('127.0.0.') ||
        hostname.endsWith('.localhost') ||
        hostname.endsWith('.lvh.me') ||
        hostname === 'lvh.me' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.');
};

export const isVercelEnvironment = (hostname) => {
    if (!hostname) return false;
    return hostname === 'vercel.app' || hostname.endsWith('.vercel.app');
};

export const isMainDomain = (hostname) => {
    if (!hostname) return false;
    if (isVercelEnvironment(hostname)) return true;
    const mainHosts = ['qr-table.kz', 'qr-menu.kz'];
    return mainHosts.some(h => hostname === h || hostname.endsWith('.' + h));
};

export const isCustomDomain = (hostname) => {
    if (!hostname) return false;
    return !isLocalEnvironment(hostname) && !isMainDomain(hostname);
};

export const getSubdomain = (hostname) => {
    if (!hostname) return null;
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.') || hostname === 'lvh.me') {
        return null;
    }

    const parts = hostname.split('.');

    if (hostname.endsWith('.localhost')) {
        return parts.length > 1 ? parts[0] : null;
    }

    if (hostname.endsWith('.lvh.me')) {
        return parts.length > 2 ? parts[0] : null;
    }

    if (isVercelEnvironment(hostname)) {
        // e.g. [cafe].qr-table.vercel.app -> 4 parts
        // qr-table-seven-eta.vercel.app -> 3 parts (root app)
        if (parts.length > 3 && parts[0] !== 'www') {
            return parts[0];
        }
        return null;
    }

    if (parts.length > 2 && parts[0] !== 'www') {
        return parts[0];
    }

    return null;
};

export const getSubdomainOrCustomDomain = () => {
    if (typeof window === 'undefined') return null;
    const hostname = window.location.hostname;

    if (isCustomDomain(hostname)) {
        return '__custom_domain__';
    }

    return getSubdomain(hostname);
};

/**
 * Generates a clean subdomain-based URL for a cafe.
 * Handles production, localhost, and lvh.me environments.
 */
export const getSubdomainUrl = (slugOrCafe, path = '') => {
    if (!slugOrCafe) return typeof window !== 'undefined' ? window.location.origin : '';

    let slug = '';
    let customDomain = '';

    if (typeof slugOrCafe === 'object' && slugOrCafe !== null) {
        slug = slugOrCafe.slug;
        customDomain = slugOrCafe.custom_domain;
    } else {
        slug = slugOrCafe;
    }

    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : '';

    // If custom domain is set, bypass subdomains and use it directly
    if (customDomain) {
        // Clean protocol/slashes from DB if present, though we already clean on save
        const cleanDomain = customDomain.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
        return `${protocol}//${cleanDomain}${path}`;
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    // 1. Handle localhost
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.')) {
        return `${protocol}//${slug}.localhost${port}${path}`;
    }

    // 2. Handle lvh.me (common for local subdomain testing)
    if (hostname.endsWith('.lvh.me') || hostname === 'lvh.me') {
        return `${protocol}//${slug}.lvh.me${port}${path}`;
    }

    // 3. Handle Vercel deployments (*.vercel.app)
    if (isVercelEnvironment(hostname)) {
        const parts = hostname.split('.');
        // If 4 parts like cafe.qr-table.vercel.app -> base is qr-table.vercel.app
        if (parts.length > 3) {
            const rootDomain = parts.slice(1).join('.');
            return `${protocol}//${slug}.${rootDomain}${port}${path}`;
        }
        // If 3 parts like qr-table-seven-eta.vercel.app -> use path routing
        return `${protocol}//${hostname}${port}/${slug}${path}`;
    }

    // 4. Handle Production (qr-table.kz, qr-menu.kz)
    let rootDomain = 'qr-table.kz';
    if (hostname.includes('qr-menu.kz')) {
        rootDomain = 'qr-menu.kz';
    } else if (hostname.includes('qr-table.kz')) {
        rootDomain = 'qr-table.kz';
    } else {
        const parts = hostname.split('.');
        if (parts.length >= 2) {
            rootDomain = parts.slice(-2).join('.');
        }
    }

    return `${protocol}//${slug}.${rootDomain}${port}${path}`;
};
